import { useFeatureFlagEnabled } from '@/features/flags/posthog-compat';
import { getEhrProviderByPvsSystemName } from '@/helpers/ehrProviderMetadata';
import { useMutateUpdateSessionEHRDetails } from '@/hooks/mutation/integration/useMutateUpdateSessionEHRDetails';
import { useMutateCreateNewSession } from '@/hooks/mutation/useMutateCreateNewSession';
import { SessionCreatedEntryPoint } from '@/models/analytics/features/sessions';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { EHRProvider } from '@heidi/schema/ml-scribe';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { toSafeGdtContext, trackGdtFileParsed } from '../telemetry';
import { GDT_FEATURE_FLAG, type GdtPatientData } from '../types';
import { useCreateGdtPatientProfile } from './useCreateGdtPatientProfile';
import { useGdtConfig } from './useGdtConfig';
import { useGdtPatient } from './useGdtPatient';

/**
 * Hook that orchestrates the complete GDT launch flow:
 * 1. Listens for `gdt-patient-received` event
 * 2. Creates a new session
 * 3. Updates session with EHR provider + patient ID and creates/links patient profile (parallel)
 * 4. Navigates to the new session
 *
 * Mount this hook once at the app level (via GdtLauncher component).
 * It is a no-op when GDT is not enabled or the local config doesn't support GDT.
 *
 * The EHR provider is derived locally from the GDT config's pvsSystemName,
 * so no backend EHR profile is required.
 */
export function useGdtLaunchComplete() {
  const router = useRouter();
  const isGdtEnabled = useFeatureFlagEnabled(GDT_FEATURE_FLAG);
  const { config, isLoading: isConfigLoading, reloadConfig } = useGdtConfig();
  const { mutateAsync: createSession, isPending: isCreatingSession } =
    useMutateCreateNewSession({
      navigate: false,
      entryPoint: SessionCreatedEntryPoint.Integration,
    });
  const { mutateAsync: updateSessionEhrDetails, isPending: isUpdatingEhr } =
    useMutateUpdateSessionEHRDetails();
  const { createOrLinkGdtPatient, isLoading: isLinkingPatient } =
    useCreateGdtPatientProfile();

  // Ref guards the entire async flow (no gap between sequential mutations)
  const isProcessingRef = useRef(false);

  // Holds a patient event that arrived before the config was ready (first-time setup race).
  // Cleared and re-processed once isEnabled becomes true.
  const pendingPatientRef = useRef<GdtPatientData | null>(null);

  // Prevents concurrent reloadConfig() IPC calls when multiple events arrive while
  // the config is stale. Reset when the reload completes (isConfigLoading → false).
  const isReloadingRef = useRef(false);

  // Derive EHR provider locally from the GDT config's pvsSystemName.
  // Falls back to Anderes_System (generic German PVS) for custom/unknown systems.
  const ehrProvider = useMemo((): EHRProvider | undefined => {
    if (!config?.pvsSystemName) return undefined;
    return (
      getEhrProviderByPvsSystemName(config.pvsSystemName) ?? EHRProvider.Anderes_System
    );
  }, [config?.pvsSystemName]);

  const isEnabled =
    !!isGdtEnabled && !isConfigLoading && !!config?.enabled && !!ehrProvider;

  // Derived pending state for UI consumers
  const isPending = isCreatingSession || isUpdatingEhr || isLinkingPatient;

  const handlePatientReceived = useCallback(
    async (patient: GdtPatientData) => {
      if (!isEnabled || !ehrProvider || !config) {
        logger.debug('[GDT] Launch ignored: GDT not enabled or no EHR provider');
        return;
      }

      if (isProcessingRef.current) {
        logger.warn('[GDT] Launch ignored: already processing a patient event');
        return;
      }

      isProcessingRef.current = true;

      try {
        logger.info('[GDT] Processing patient launch');
        trackGdtFileParsed(toSafeGdtContext(config), patient.recordType);

        // Step 1: Create session
        const sessionResult = await createSession();
        const sessionId = sessionResult.data.session_id;

        // Steps 2-3 are best-effort and independent — run in parallel.
        // If they fail, still navigate so the user isn't left with an orphaned session.
        const [ehrResult, patientResult] = await Promise.allSettled([
          updateSessionEhrDetails({
            sessionId,
            provider: ehrProvider,
            patientId: patient.patientId,
          }),
          createOrLinkGdtPatient(patient, [sessionId], ehrProvider),
        ]);

        const failures = [ehrResult, patientResult].filter(
          (r) => r.status === 'rejected'
        );

        if (failures.length > 0) {
          logger.error('[GDT] Partial failure during session setup', {
            failedSteps: failures.length,
          });
          toast({
            tone: 'destructive',
            title: 'Session created with incomplete data',
            description:
              'The session was created but some patient details could not be linked. You may need to add them manually.',
          });
        }

        logger.info('[GDT] Session created, navigating');

        // Step 4: Navigate to the new session (always, even on partial failure)
        await router.push(`/scribe/session/${sessionId}`);
      } catch {
        logger.error('[GDT] Failed to create session');
      } finally {
        isProcessingRef.current = false;
      }
    },
    [
      isEnabled,
      ehrProvider,
      config,
      createSession,
      updateSessionEhrDetails,
      createOrLinkGdtPatient,
      router,
    ]
  );

  // Wrap async handler in a void callback for useGdtPatient's sync signature.
  // If config is stale (isEnabled = false) AND reloading could help (flag is on and
  // no load is already in-flight), store the patient for retry and trigger a fresh
  // config load from disk. Guards prevent redundant IPC calls when the flag is off,
  // config is already loading, or a reload is already in-flight.
  const onPatientReceived = useCallback(
    (patient: GdtPatientData) => {
      if (!isEnabled) {
        pendingPatientRef.current = patient;
        if (!!isGdtEnabled && !isConfigLoading && !isReloadingRef.current) {
          isReloadingRef.current = true;
          void reloadConfig().finally(() => {
            isReloadingRef.current = false;
          });
        }
        return;
      }
      void handlePatientReceived(patient);
    },
    [isEnabled, isGdtEnabled, isConfigLoading, handlePatientReceived, reloadConfig]
  );

  // Once isEnabled becomes true, process any patient event that arrived while the
  // config was still loading (first-time setup race condition).
  useEffect(() => {
    if (isEnabled && pendingPatientRef.current) {
      const pending = pendingPatientRef.current;
      pendingPatientRef.current = null;
      void handlePatientReceived(pending);
    }
  }, [isEnabled, handlePatientReceived]);

  // Always register the listener so events are never missed due to isEnabled timing.
  useGdtPatient(onPatientReceived);

  return { isEnabled, isPending };
}
